
import React, { createContext, useState, useRef, useContext, useEffect } from 'react';
import { TaskContext } from './TaskContext';

export const TimerContext = createContext();

export const TimerProvider = ({ children }) => {
  const { selectedTask } = useContext(TaskContext);
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('timeLogger_mode');
    return savedMode ? savedMode : 'free';
  });
  const [time, setTime] = useState(0);
  const [isActive, setIsActive] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [pomodoroDuration, setPomodoroDuration] = useState(() => {
    const savedDuration = localStorage.getItem('timeLogger_pomodoroDuration');
    return savedDuration ? parseInt(savedDuration) : 25 * 60;
  });
  const timerRef = useRef(null);
  const completedRef = useRef(false);
  const [logs, setLogs] = useState(() => {
    const savedLogs = localStorage.getItem('timeLogger_logs');
    return savedLogs ? JSON.parse(savedLogs) : [];
  });

  useEffect(() => {
    localStorage.setItem('timeLogger_mode', mode);
  }, [mode]);

  useEffect(() => {
    localStorage.setItem('timeLogger_pomodoroDuration', pomodoroDuration.toString());
  }, [pomodoroDuration]);

  useEffect(() => {
    localStorage.setItem('timeLogger_logs', JSON.stringify(logs));
  }, [logs]);

  const startTimer = () => {
    setIsActive(true);
    setIsPaused(false);
    completedRef.current = false;
    logAction('start');
    timerRef.current = setInterval(() => {
      setTime((prevTime) => {
        if (mode === 'pomodoro') {
          if (prevTime <= 1) {
            clearInterval(timerRef.current);
            setIsActive(false);
            setIsPaused(false);
            if (!completedRef.current) {
              completedRef.current = true;
              logAction('complete');
            }
            return 0;
          }
          return prevTime - 1;
        } else {
          return prevTime + 1;
        }
      });
    }, 1000);
  };

  const stopTimer = () => {
    clearInterval(timerRef.current);
    setIsActive(false);
    setIsPaused(false);
    completedRef.current = false;
    logAction('stop');
    setTime(0);
  };

  const pauseTimer = () => {
    if (isActive && !isPaused) {
      clearInterval(timerRef.current);
      setIsPaused(true);
      logAction('pause');
    }
  };

  const resumeTimer = () => {
    if (isPaused) {
      setIsPaused(false);
      startTimer();
    }
  };

  const startPomodoroTimer = () => {
    setTime(pomodoroDuration);
    completedRef.current = false;
    startTimer();
  };

  const logAction = (action) => {
    const log = {
      id: Date.now().toString(),
      action,
      time: new Date(),
      task: selectedTask ? selectedTask.name : 'No task selected',
      duration: action === 'stop' ? time : 0,
      mode: mode,
    };
    setLogs((prevLogs) => [...prevLogs, log]);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  const importLogs = (importedLogs) => {
    setLogs(importedLogs);
  };

  const exportLogs = () => {
    return {
      logs,
      exportDate: new Date().toISOString(),
    };
  };

  const exportAllData = () => {
    return {
      logs,
      mode,
      pomodoroDuration,
      exportDate: new Date().toISOString(),
    };
  };

  return (
    <TimerContext.Provider
      value={{
        mode,
        setMode,
        time,
        isActive,
        isPaused,
        pomodoroDuration,
        setPomodoroDuration,
        startTimer,
        startPomodoroTimer,
        stopTimer,
        pauseTimer,
        resumeTimer,
        logs,
        clearLogs,
        importLogs,
        exportLogs,
        exportAllData,
      }}
    >
      {children}
    </TimerContext.Provider>
  );
};
